import React, { useEffect, useState, forwardRef, useCallback, useMemo } from 'react';
import i18n from '../../../i18n';

const Duration = forwardRef(({
  fieldName,
  wrapperClass = '',
  label,
  hideLabel = false,
  allowNull = true,
  value = '',
  errors = [],
  helpText = '',
  parent = '',
  autofocus = false,
  disabled = false,
  format = 'd h:m',
  width = '',
  style = {},
  onChange = () => {},
  pk = null
}, ref) => {
  // Parse format to determine which fields to show
  const formatConfig = useMemo(() => {
    const hasDays = format.includes('d');
    const hasHours = format.includes('h');
    const hasMinutes = format.includes('m');
    const hasSeconds = format.includes('s');
    
    return {
      hasDays,
      hasHours,
      hasMinutes,
      hasSeconds
    };
  }, [format]);

  // Parse value based on format
  const parseValue = useCallback((val) => {
    if (!val || typeof val !== 'string' || val.trim() === '') {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const trimmed = val.trim();
    let days = 0, hours = 0, minutes = 0, seconds = 0;

    // Check if value has space (indicating days)
    if (trimmed.includes(' ')) {
      const [daysPart, timePart] = trimmed.split(' ');
      days = parseInt(daysPart) || 0;
      const timeParts = timePart.split(':');
      if (timeParts.length >= 1) hours = parseInt(timeParts[0]) || 0;
      if (timeParts.length >= 2) minutes = parseInt(timeParts[1]) || 0;
      if (timeParts.length >= 3) seconds = parseInt(timeParts[2]) || 0;
    } else {
      // No space, so it's just time (h:m:s or m:s)
      const timeParts = trimmed.split(':');
      if (timeParts.length === 2) {
        // m:s format
        minutes = parseInt(timeParts[0]) || 0;
        seconds = parseInt(timeParts[1]) || 0;
      } else if (timeParts.length >= 3) {
        // h:m:s format
        hours = parseInt(timeParts[0]) || 0;
        minutes = parseInt(timeParts[1]) || 0;
        seconds = parseInt(timeParts[2]) || 0;
      }
    }

    return { days, hours, minutes, seconds };
  }, []);

  const [duration, setDuration] = useState(() => parseValue(value));

  // Parse initial value
  useEffect(() => {
    setDuration(parseValue(value));
  }, [value, parseValue]);

  // Format duration based on format prop
  const formatDuration = useCallback((dur) => {
    const { hasDays, hasHours, hasMinutes, hasSeconds } = formatConfig;
    
    // Always set non-existing parts to 0
    const d = hasDays ? (dur.days || 0) : 0;
    const h = hasHours ? (dur.hours || 0) : 0;
    const m = hasMinutes ? (dur.minutes || 0) : 0;
    const s = hasSeconds ? (dur.seconds || 0) : 0;

    // Build the full string based on format
    if (format === 'd h:m') {
      return `${d} ${h}:${m}`;
    } else if (format === 'd h:m:s') {
      return `${d} ${h}:${m}:${s}`;
    } else if (format === 'h:m:s') {
      return `${h}:${m}:${s}`;
    } else if (format === 'm:s') {
      return `${m}:${s}`;
    }
    
    // Default fallback
    return `${d} ${h}:${m}:${s}`;
  }, [format, formatConfig]);

  // Handle individual field changes
  const handleFieldChange = useCallback((field, newValue) => {
    const numValue = Math.max(0, parseInt(newValue) || 0);
    const newDuration = { ...duration, [field]: numValue };
    
    setDuration(newDuration);
    
    // Call onChange with formatted value
    onChange({
      'name': fieldName,
      'value': formatDuration(newDuration),
      'label': formatDuration(newDuration)
    });
  }, [duration, fieldName, onChange, formatDuration]);

  // Handle input change
  const handleInputChange = useCallback((field, event) => {
    const newValue = event.target.value;
    handleFieldChange(field, newValue);
  }, [handleFieldChange]);

  // Handle input blur for validation
  const handleInputBlur = useCallback((field, event) => {
    const newValue = event.target.value;
    const numValue = Math.max(0, parseInt(newValue) || 0);
    handleFieldChange(field, numValue);
  }, [handleFieldChange]);

  const { hasDays, hasHours, hasMinutes, hasSeconds } = formatConfig;
  
  // Calculate column width based on number of visible fields
  const visibleFieldsCount = [hasDays, hasHours, hasMinutes, hasSeconds].filter(Boolean).length;
  const colClass = visibleFieldsCount > 0 ? `col-${Math.floor(12 / visibleFieldsCount)}` : 'col-3';

  return (
      <div className="row g-2">
        {hasDays && (
          <div className={colClass}>
            <label className="mb-0 form-label small text-muted">{i18n.t('Days')}</label>
            <input
              ref={fieldName === 'days' ? ref : null}
              id={pk ? `${pk}_${fieldName}_days` : `${fieldName}_days`}
              name={`${fieldName}_days`}
              type="number"
              min="0"
              className={`form-control form-control-sm ${errors.length > 0 ? 'is-invalid' : ''}`}
              value={duration.days}
              disabled={disabled}
              data-char_select_parent={parent}
              onChange={(e) => handleInputChange('days', e)}
              onBlur={(e) => handleInputBlur('days', e)}
              placeholder="0"
            />
          </div>
        )}
        {hasHours && (
          <div className={colClass}>
            <label className="mb-0 form-label small text-muted">{i18n.t('Hours')}</label>
            <input
              id={pk ? `${pk}_${fieldName}_hours` : `${fieldName}_hours`}
              name={`${fieldName}_hours`}
              type="number"
              min="0"
              max="23"
              className={`form-control form-control-sm ${errors.length > 0 ? 'is-invalid' : ''}`}
              value={duration.hours}
              disabled={disabled}
              data-char_select_parent={parent}
              onChange={(e) => handleInputChange('hours', e)}
              onBlur={(e) => handleInputBlur('hours', e)}
              placeholder="0"
            />
          </div>
        )}
        {hasMinutes && (
          <div className={colClass}>
            <label className="mb-0 form-label small text-muted">{i18n.t('Minutes')}</label>
            <input
              id={pk ? `${pk}_${fieldName}_minutes` : `${fieldName}_minutes`}
              name={`${fieldName}_minutes`}
              type="number"
              min="0"
              max="59"
              className={`form-control form-control-sm ${errors.length > 0 ? 'is-invalid' : ''}`}
              value={duration.minutes}
              disabled={disabled}
              data-char_select_parent={parent}
              onChange={(e) => handleInputChange('minutes', e)}
              onBlur={(e) => handleInputBlur('minutes', e)}
              placeholder="0"
            />
          </div>
        )}
        {hasSeconds && (
          <div className={colClass}>
            <label className="mb-0 form-label small text-muted">{i18n.t('Seconds')}</label>
            <input
              id={pk ? `${pk}_${fieldName}_seconds` : `${fieldName}_seconds`}
              name={`${fieldName}_seconds`}
              type="number"
              min="0"
              max="59"
              className={`form-control form-control-sm ${errors.length > 0 ? 'is-invalid' : ''}`}
              value={duration.seconds}
              disabled={disabled}
              data-char_select_parent={parent}
              onChange={(e) => handleInputChange('seconds', e)}
              onBlur={(e) => handleInputBlur('seconds', e)}
              placeholder="0"
            />
          </div>
        )}
      </div>
  );
});

export default Duration;
